import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { scaleTime } from "d3-scale";
import { discontinuousTimeScaleProviderBuilder } from "react-stockcharts/lib/scale";
import { ChartCanvas, Chart } from "react-stockcharts";
import { AreaSeries, BarSeries } from "react-stockcharts/lib/series";
import { XAxis, YAxis } from "react-stockcharts/lib/axes";
import { fitWidth } from "react-stockcharts/lib/helper";
import { last, first } from "react-stockcharts/lib/utils";
import { v2API } from "@voxmarkets/api-sdk";
// import { Brush } from "react-stockcharts/lib/interactive";
import {
  getNewChartConfig,
  getChartConfigWithUpdatedYScales,
  getCurrentCharts,
  getCurrentItem
} from "react-stockcharts/lib/utils/ChartDataUtil";

import {
  InteractiveYCoordinate,
  ClickCallback
} from "react-stockcharts/lib/interactive";
import { HoverTooltip, OHLCTooltip } from "react-stockcharts/lib/tooltip";
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";

import Slider from "rc-slider";
import { Spacing } from "./vox-web/components/Layout";
import PreviewChart from "./PreviewChart";
import ChartRangeSlider from "./ChartRangeSlider";
import { formatNumber } from "./vox-web/utils";

const dateFormat = timeFormat("%Y-%m-%d");
const numberFormat = format(".2f");

function tooltipSVG({ fontFamily, fontSize, fontFill }, content) {
  const PADDING = 5;
  const X = 10;
  const Y = 10;
  const tspans = [];
  const startY = Y + fontSize * 0.9;

  for (let i = 0; i < content.y.length; i++) {
    const y = content.y[i];
    const textY = startY + fontSize * (i + 1);

    tspans.push(
      <tspan key={`L-${i}`} x={X} y={textY} fill={y.stroke}>
        {y.label}
      </tspan>
    );
    tspans.push(<tspan key={i}>: </tspan>);
    tspans.push(<tspan key={`V-${i}`}>{y.value}</tspan>);
  }
  return (
    <text fontFamily={fontFamily} fontSize={13} fill={fontFill}>
      <tspan x={X} y={startY} fontWeight="bold">
        {content.x}
      </tspan>
      {tspans}
    </text>
  );
}

function tooltipContent(ys) {
  return ({ currentItem, xAccessor }) => {
    return {
      x: dateFormat(xAccessor(currentItem)),
      y: [
        {
          label: "open",
          value: currentItem.open && formatNumber(currentItem.open, 2)
        },
        {
          label: "high",
          value: currentItem.high && formatNumber(currentItem.high, 2)
        },
        {
          label: "low",
          value: currentItem.low && formatNumber(currentItem.low, 2)
        },
        {
          label: "close",
          value: currentItem.close && formatNumber(currentItem.close, 2)
        },
        {
          label: "volume",
          value: currentItem.volume && formatNumber(currentItem.volume, 0)
        }
      ]
    };
  };
}

const rgba = (rgb, opacity = 1) => `rgba(${rgb},${opacity})`;
// borderColor={this.props.axisColor}
//                 textColor={this.props.textColor}
//                 activeColor={this.props.lineColor}

const Button = styled.button`
  height: 29px;
  font-size: 14px;
  font-family: "Lato", sans-serif;
  color: ${props => rgba(props.textColor)};
  font-size: 1rem;
  border: 1px solid ${props => rgba(props.borderColor)};
  outline: none;
  background-color: transparent;
  padding: 0.2em 0.3em;
  width: 55px;
  cursor: pointer;

  &:hover {
    background-color: ${props => rgba(props.activeColor, 0.8)};
    color: #fff;
  }
  &:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }

  ${props =>
    props.active &&
    css`
      background-color: ${props => rgba(props.activeColor)};
      color: #fff;
    `};
`;

const ButtonsWrapper = styled.div`
  text-align: center;
  ${Button} + ${Button} {
    border-left: none;
  }
`;

class AreaChart extends React.Component {
  static defaultProps = {
    lineColor: "20, 98, 125",
    axisColor: "20, 98, 125",
    textColor: "50, 50, 50",
    bgColor: "0, 0, 0"
  };

  // static propTypes = {
  //   data: PropTypes.array.isRequired,
  //   width: PropTypes.number.isRequired,
  //   ratio: PropTypes.number.isRequired
  // };

  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      ...this.getChartState(props.data, 0),
      size: 252,
      range: [props.data.length - 252, props.data.length]
    };
  }

  getChartState = (chartData, initialIndex) => {
    const xScaleProvider = discontinuousTimeScaleProviderBuilder()
      .initialIndex(initialIndex)
      .inputDateAccessor(d => d.date);

    return {
      initialIndex,
      ...xScaleProvider(chartData)
    };
  };

  // componentDidMount() {
  //   this.zoomToDays(60);
  // }

  zoomToDays = days => {
    const right = this.state.xAccessor(last(this.state.data));
    this.setState({ size: days, range: [right - days, right] });
  };

  handlePan = e => {
    //console.log("PAN", e);
    const left = this.state.xAccessor(first(e.plotData));
    const right = this.state.xAccessor(last(e.plotData));

    this.setState({
      range: [left, right]
    });
  };

  handleRangeChange = range => {
    this.setState({ range });
  };

  render() {
    const { type, width, ratio } = this.props;
    const { data, xScale, xAccessor, displayXAccessor } = this.state;

    const margin = { left: 20, right: 40, top: 10, bottom: 30 };

    const height = 338;
    const gridHeight = height - margin.top - margin.bottom;
    const gridWidth = width - margin.left - margin.right;

    const showGrid = true;
    const yGrid = showGrid ? { innerTickSize: -1 * gridWidth } : {};
    const xGrid = showGrid ? { innerTickSize: -1 * gridHeight } : {};
    // const size = this.node.current
    //   ? this.node.current.state.plotData.length
    //   : 0;

    // // if (this.node.current) {
    // //   console.log("size", this.node.current.state.plotData.length);
    // // }

    // console.log("size", size);

    return (
      <div>
        <div style={{ position: "absolute" }}>
          <svg>
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop
                  offset="0%"
                  stopColor={rgba(this.props.lineColor)}
                  stopOpacity=".4"
                />
                <stop
                  offset="30%"
                  stopColor={rgba(this.props.lineColor)}
                  stopOpacity=".2"
                />
                <stop
                  offset="100%"
                  stopColor={rgba(this.props.lineColor)}
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <ChartCanvas
          ref={this.node}
          ratio={ratio}
          width={width}
          height={300}
          margin={margin}
          seriesName="MSFT"
          data={data}
          type="svg"
          xScale={xScale}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          mouseMoveEvent={true}
          panEvent={true}
          zoomEvent={false}
          clamp={true}
          pointsPerPxThreshold={2}
          minPointsPerPxThreshold={2 / 1000}
          xExtents={this.state.range}
        >
          <Chart
            id={0}
            yExtents={d => d.close}
            padding={{ top: 10, right: 0, bottom: 50, left: 0 }}
          >
            <ClickCallback onPan={this.handlePan} onPanEnd={this.handlePan} />
            <XAxis
              fontFamily="Roboto Condensed"
              axisAt="bottom"
              orient="bottom"
              {...xGrid}
              stroke={rgba(this.props.axisColor)}
              tickPadding={10}
              tickStrokeWidth={1}
              outerTickSize={0}
              tickStrokeDasharray={"ShortDash"}
              tickStrokeOpacity={0.2}
              tickStrokeWidth={1}
              tickStroke={rgba(this.props.textColor)}
              zoomEnabled={false}
            />
            <YAxis
              fontFamily="Roboto Condensed"
              axisAt="right"
              orient="right"
              {...yGrid}
              tickPadding={10}
              outerTickSize={0}
              stroke={rgba(this.props.axisColor)}
              strokeWidth={1}
              tickStrokeDasharray={"ShortDash"}
              tickStrokeOpacity={0.2}
              tickStrokeWidth={1}
              tickStroke={rgba(this.props.textColor)}
              zoomEnabled={false}
            />
            <AreaSeries
              yAccessor={d => d.close}
              fill="url(#grad1)"
              stroke={rgba(this.props.lineColor)}
              strokeWidth={1.5}
              strokeOpacity={1}
              opacity={1}
            />
            <HoverTooltip
              tooltipContent={tooltipContent()}
              tooltipSVG={tooltipSVG}
              fontFamily="Lato, sans-serif"
              stroke={rgba(this.props.axisColor)}
              strokeOpacity={1}
              fill="#ffffff"
              opacity={0.7}
              fontFill="#135d80"
              fontSize={15}
              bgFill={rgba(this.props.axisColor)}
            />
          </Chart>
          <Chart
            id={1}
            height={50}
            yExtents={d => d.volume}
            origin={(w, h) => [0, h - 50]}
          >
            <BarSeries
              yAccessor={d => d.volume}
              fill={rgba(this.props.lineColor)}
              widthRatio={0.8}
              stroke={false}
              opacity={1}
            />
          </Chart>
        </ChartCanvas>
        <Spacing top={1}>
          <ButtonsWrapper>
            {data.length >= 5 && (
              <Button
                onClick={e => this.zoomToDays(5)}
                active={this.state.size === 5}
                borderColor={this.props.axisColor}
                textColor={this.props.textColor}
                activeColor={this.props.lineColor}
              >
                5d
              </Button>
            )}
            {data.length >= 60 && (
              <Button
                onClick={e => this.zoomToDays(60)}
                active={this.state.size === 60}
                borderColor={this.props.axisColor}
                textColor={this.props.textColor}
                activeColor={this.props.lineColor}
              >
                3m
              </Button>
            )}
            {data.length >= 120 && (
              <Button
                onClick={e => this.zoomToDays(120)}
                active={this.state.size === 120}
                borderColor={this.props.axisColor}
                textColor={this.props.textColor}
                activeColor={this.props.lineColor}
              >
                6m
              </Button>
            )}
            {data.length >= 252 * 1 && (
              <Button
                onClick={e => this.zoomToDays(252)}
                active={this.state.size === 252}
                borderColor={this.props.axisColor}
                textColor={this.props.textColor}
                activeColor={this.props.lineColor}
              >
                1y
              </Button>
            )}
            {data.length >= 252 * 2 && (
              <Button
                onClick={e => this.zoomToDays(252 * 2)}
                active={this.state.size === 252 * 2}
                borderColor={this.props.axisColor}
                textColor={this.props.textColor}
                activeColor={this.props.lineColor}
              >
                2y
              </Button>
            )}
            {data.length >= 252 * 5 && (
              <Button
                onClick={e => this.zoomToDays(252 * 5)}
                active={this.state.size === 252 * 5}
                borderColor={this.props.axisColor}
                textColor={this.props.textColor}
                activeColor={this.props.lineColor}
              >
                5y
              </Button>
            )}
            <Button
              onClick={e => this.zoomToDays(data.length)}
              active={this.state.size === data.length}
              borderColor={this.props.axisColor}
              textColor={this.props.textColor}
              activeColor={this.props.lineColor}
            >
              Max
            </Button>
          </ButtonsWrapper>
        </Spacing>
        <Spacing top={1} bottom={1}>
          <Poc>
            <div>
              <ChartRangeSlider
                defaultValue={this.state.range}
                value={this.state.range}
                onChange={this.handleRangeChange}
                allowCross={false}
                min={0}
                max={data.length}
                pushable={10}
              />
            </div>
            <div>
              <PreviewChart
                data={this.props.data}
                textColor={this.props.textColor}
                lineColor={this.props.lineColor}
                axisColor={this.props.axisColor}
              />
            </div>
          </Poc>
        </Spacing>
      </div>
    );
  }
}

const Poc = styled.div`
  position: relative;
  height: 60px;
  > * {
    &:first-child {
      z-index: 999999;
      left: 20px;
      right: 40px;
      overflow: hidden;
    }
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
`;

// const Layer = styled.div`
//   position: absolute;
//   top: 0;
//   left: 20px;
//   right: 40px;
//   z-index: 999999999999;
//   height: 60px;
// `;

AreaChart = fitWidth(AreaChart);

//-----------------------------------------------
//----------------------------------------------- Range
//-----------------------------------------------

//-----------------------------------------------
//-----------------------------------------------
//-----------------------------------------------
class ChartPro extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: []
    };
  }

  componentDidMount() {
    v2API.Charts.getByListing(this.props.listingId, 10000).then(data => {
      this.setState({
        data: data
      });
    });
  }

  render() {
    return (
      <div>
        {this.state.data.length > 0 && (
          <>
            <AreaChart
              {...this.props}
              data={this.state.data}
              from={Date.now()}
            />
          </>
        )}
      </div>
    );
  }
}

export default ChartPro;
